import { useState } from 'react';
import { useCreateTreatmentPlan } from 'hooks/usePlans';
import {
  Box,
  Center,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
  Badge,
  Select,
  Divider,
  Grid,
  GridItem,
  List,
  ListItem,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import CustomDrawer from 'components/ui/Drawer';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_PATIENTS = [
  { id: 'p1', nombres: 'Carlos', apellidos: 'Mendoza Ríos',     cedula: '1721456789' },
  { id: 'p2', nombres: 'María',  apellidos: 'Torres Salcedo',   cedula: '0912345678' },
  { id: 'p3', nombres: 'Andrés', apellidos: 'Villacrés Ponce',  cedula: '1803456789' },
  { id: 'p4', nombres: 'Lucía',  apellidos: 'Barrera Naranjo',  cedula: '1712345678' },
];

const MOCK_EPISODES: Record<string, { id: string; codigoHc: string; motivoConsulta: string; diagnostico: string | null; estado: string }[]> = {
  p1: [
    { id: 'e1', codigoHc: 'HC-2024-001', motivoConsulta: 'Dolor lumbar crónico',               diagnostico: 'Hernia discal L4-L5',               estado: 'en_tratamiento' },
    { id: 'e2', codigoHc: 'HC-2024-002', motivoConsulta: 'Rehabilitación post-quirúrgica rodilla', diagnostico: 'Lesión LCA post-operatorio',     estado: 'abierto' },
  ],
  p2: [
    { id: 'e3', codigoHc: 'HC-2024-015', motivoConsulta: 'Tendinitis del manguito rotador',    diagnostico: 'Tendinopatía supraespinoso derecho', estado: 'abierto' },
  ],
  p3: [
    { id: 'e4', codigoHc: 'HC-2024-032', motivoConsulta: 'Esguince de tobillo grado II',       diagnostico: 'Esguince ligamento lateral externo', estado: 'en_tratamiento' },
    { id: 'e5', codigoHc: 'HC-2024-033', motivoConsulta: 'Cervicalgia tensional',              diagnostico: null,                                 estado: 'abierto' },
  ],
  p4: [
    { id: 'e6', codigoHc: 'HC-2024-047', motivoConsulta: 'Fractura de radio distal',           diagnostico: 'Fractura Colles post-reducción',     estado: 'en_tratamiento' },
  ],
};

const MOCK_AUTO_PATIENT = MOCK_PATIENTS[0];
const MOCK_AUTO_EPISODE  = MOCK_EPISODES.p1[0];

const ESTADO_CONFIG: Record<string, { label: string; color: string }> = {
  abierto:         { label: 'Abierto',         color: 'blue'  },
  en_tratamiento:  { label: 'En Tratamiento',  color: 'green' },
};

// ─── Tipos ────────────────────────────────────────────────────────────────────

type Modo = 'ficha' | 'externo';

interface FormData {
  totalSesiones:       string;
  frecuencia:          string;
  objetivoCortoPlayzo: string;
  objetivoLargoPlayzo: string;
}

// ─── Iconos inline ────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconClipboard() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// ─── PatientEpisodeCard ───────────────────────────────────────────────────────

interface PatientEpisodeCardProps {
  patientName: string;
  cedula:      string;
  episodeCode: string;
  motivo:      string;
  diagnostico: string | null;
  estado:      string;
}

function PatientEpisodeCard({ patientName, cedula, episodeCode, motivo, diagnostico, estado }: PatientEpisodeCardProps) {
  const cardBg       = useColorModeValue('gray.50',  'gray.700');
  const borderColor  = useColorModeValue('gray.200', 'gray.600');
  const labelColor   = useColorModeValue('gray.500', 'gray.400');
  const valueColor   = useColorModeValue('gray.800', 'gray.100');

  const accentBg     = useColorModeValue('indigo.50',  'rgba(99,102,241,0.15)');
  const accentBorder = useColorModeValue('indigo.200', 'indigo.600');
  const accentText   = useColorModeValue('indigo.700', 'indigo.300');

  const purpleBg     = useColorModeValue('purple.50',  'rgba(168,85,247,0.15)');
  const purpleBorder = useColorModeValue('purple.200', 'purple.600');
  const purpleText   = useColorModeValue('purple.600', 'purple.300');

  const estadoInfo = ESTADO_CONFIG[estado] ?? { label: estado, color: 'gray' };

  return (
    <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="10px" overflow="hidden">
      {/* Paciente */}
      <Box px="16px" py="12px" borderBottom="1px solid" borderColor={borderColor}>
        <HStack spacing="10px">
          <Box w="32px" h="32px" borderRadius="8px" bg={accentBg} border="1px solid" borderColor={accentBorder} display="flex" alignItems="center" justifyContent="center" color={accentText} flexShrink={0}>
            <IconUser />
          </Box>
          <VStack align="flex-start" spacing="0">
            <Text fontSize="sm" fontWeight="700" color={valueColor} lineHeight="1.3">{patientName}</Text>
            <Text fontSize="xs" color={labelColor}>CI: {cedula}</Text>
          </VStack>
        </HStack>
      </Box>
      {/* Episodio */}
      <Box px="16px" py="12px">
        <HStack spacing="10px" align="flex-start">
          <Box w="32px" h="32px" borderRadius="8px" bg={purpleBg} border="1px solid" borderColor={purpleBorder} display="flex" alignItems="center" justifyContent="center" color={purpleText} flexShrink={0} mt="2px">
            <IconClipboard />
          </Box>
          <VStack align="flex-start" spacing="4px" flex="1">
            <HStack spacing="8px">
              <Text fontSize="xs" fontWeight="600" color={labelColor}>{episodeCode}</Text>
              <Badge colorScheme={estadoInfo.color} fontSize="10px" px="6px" py="1px" borderRadius="4px">{estadoInfo.label}</Badge>
            </HStack>
            <Text fontSize="sm" fontWeight="600" color={valueColor} lineHeight="1.3">{motivo}</Text>
            {diagnostico && <Text fontSize="xs" color={labelColor}>Dx: {diagnostico}</Text>}
          </VStack>
        </HStack>
      </Box>
    </Box>
  );
}

// ─── EpisodeSelector ─────────────────────────────────────────────────────────

interface EpisodeSelectorProps {
  patientId:         string;
  selectedEpisodeId: string | null;
  onSelect:          (id: string) => void;
}

function EpisodeSelector({ patientId, selectedEpisodeId, onSelect }: EpisodeSelectorProps) {
  const episodes       = MOCK_EPISODES[patientId] ?? [];
  const borderColor    = useColorModeValue('gray.200',  'gray.600');
  const hoverBg        = useColorModeValue('gray.50',   'gray.700');
  const selectedBg     = useColorModeValue('indigo.50', 'rgba(99,102,241,0.15)');
  const selectedBorder = useColorModeValue('indigo.300','indigo.500');
  const labelColor     = useColorModeValue('gray.500',  'gray.400');
  const valueColor     = useColorModeValue('gray.800',  'gray.100');
  const checkColor     = useColorModeValue('indigo.500','indigo.300');
  const emptyBg        = useColorModeValue('orange.50', 'rgba(237,137,54,0.1)');
  const emptyBorder    = useColorModeValue('orange.200','orange.600');
  const emptyText      = useColorModeValue('orange.700','orange.300');

  if (episodes.length === 0) {
    return (
      <Box p="12px" borderRadius="8px" bg={emptyBg} border="1px solid" borderColor={emptyBorder}>
        <Text fontSize="sm" color={emptyText}>Paciente sin episodios clínicos activos disponibles.</Text>
      </Box>
    );
  }

  return (
    <VStack spacing="6px" align="stretch">
      {episodes.map((ep) => {
        const isSelected = ep.id === selectedEpisodeId;
        const estadoInfo = ESTADO_CONFIG[ep.estado] ?? { label: ep.estado, color: 'gray' };
        return (
          <Box
            key={ep.id}
            px="14px" py="10px"
            borderRadius="8px"
            border="1px solid"
            borderColor={isSelected ? selectedBorder : borderColor}
            bg={isSelected ? selectedBg : 'transparent'}
            cursor="pointer"
            onClick={() => onSelect(ep.id)}
            _hover={{ bg: isSelected ? selectedBg : hoverBg }}
            transition="all 0.15s"
          >
            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing="2px">
                <HStack spacing="6px">
                  <Text fontSize="xs" color={labelColor} fontWeight="600">{ep.codigoHc}</Text>
                  <Badge colorScheme={estadoInfo.color} fontSize="10px" px="5px" py="1px" borderRadius="4px">{estadoInfo.label}</Badge>
                </HStack>
                <Text fontSize="sm" fontWeight="600" color={valueColor}>{ep.motivoConsulta}</Text>
                {ep.diagnostico && <Text fontSize="xs" color={labelColor}>Dx: {ep.diagnostico}</Text>}
              </VStack>
              {isSelected && <Box color={checkColor} mt="2px"><IconCheck /></Box>}
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TreatmentPlansPreview() {
  const [isOpen, setIsOpen] = useState(false);
  const [modo,   setModo]   = useState<Modo>('ficha');

  const toast = useToast();
  const createPlan = useCreateTreatmentPlan();

  const [searchQuery,       setSearchQuery]       = useState('');
  const [showResults,       setShowResults]       = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    totalSesiones:       '10',
    frecuencia:          '3',
    objetivoCortoPlayzo: '',
    objetivoLargoPlayzo: '',
  });

  // ── Color tokens ──
  const bg             = useColorModeValue('gray.50',  'gray.900');
  const headerBg       = useColorModeValue('white',    'gray.800');
  const badgeBg        = useColorModeValue('rgba(99, 102, 241, 0.1)',  'rgba(99, 102, 241, 0.2)');
  const badgeColor     = useColorModeValue('indigo.600','indigo.300');
  const borderColor    = useColorModeValue('gray.200', 'gray.700');
  const sectionLabel   = useColorModeValue('gray.500', 'gray.400');
  const resultsBg      = useColorModeValue('white',    'gray.800');
  const resultHover    = useColorModeValue('gray.50',  'gray.700');
  const resultBorder   = useColorModeValue('gray.200', 'gray.600');
  const resultIconBg   = useColorModeValue('gray.100', 'gray.700');
  const resultIconText = useColorModeValue('gray.500', 'gray.400');
  const resultText     = useColorModeValue('gray.800', 'gray.100');
  const modeBg         = useColorModeValue('gray.100', 'gray.800');
  const modeActiveBg   = useColorModeValue('white',    'gray.700');
  const modeActiveText = useColorModeValue('gray.800', 'gray.100');
  const modeIdleText   = useColorModeValue('gray.500', 'gray.400');
  const hintBg         = useColorModeValue('blue.50',  'rgba(59,130,246,0.08)');
  const hintBorder     = useColorModeValue('blue.200', 'blue.700');
  const hintText       = useColorModeValue('blue.700', 'blue.300');

  // ── Derived ──
  const filteredPatients = searchQuery.length >= 2
    ? MOCK_PATIENTS.filter(p =>
        `${p.nombres} ${p.apellidos}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.cedula.includes(searchQuery)
      )
    : [];

  const selectedPatient = MOCK_PATIENTS.find(p => p.id === selectedPatientId) ?? null;
  const selectedEpisode = selectedPatientId
    ? (MOCK_EPISODES[selectedPatientId] ?? []).find(e => e.id === selectedEpisodeId) ?? null
    : null;

  const showPlanForm = modo === 'ficha' || (modo === 'externo' && selectedPatientId !== null && selectedEpisodeId !== null);

  // ── Handlers ──
  const handleModoChange = (m: Modo) => {
    setModo(m);
    setSearchQuery('');
    setShowResults(false);
    setSelectedPatientId(null);
    setSelectedEpisodeId(null);
  };

  const handleSelectPatient = (patientId: string) => {
    const p = MOCK_PATIENTS.find(x => x.id === patientId)!;
    setSelectedPatientId(patientId);
    setSelectedEpisodeId(null);
    setSearchQuery(`${p.nombres} ${p.apellidos}`);
    setShowResults(false);
  };

  const FREQ_LABEL: Record<string, string> = {
    '1': '1 vez por semana',
    '2': '2 veces por semana',
    '3': '3 veces por semana',
    '4': '4 veces por semana',
    '5': '5 veces por semana',
  };

  const getEpisodeId = () =>
    modo === 'ficha' ? MOCK_AUTO_EPISODE.id : (selectedEpisodeId ?? '');

  const handleSave = () => {
    const episodeId = getEpisodeId();
    if (!episodeId) return;
    if (!formData.objetivoCortoPlayzo.trim() || !formData.objetivoLargoPlayzo.trim()) {
      toast({ title: 'Completa los objetivos terapéuticos.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    createPlan.mutate(
      {
        clinicalEpisodeId:    episodeId,
        totalSessions:        Math.max(1, parseInt(formData.totalSesiones, 10) || 1),
        frequencyDescription: FREQ_LABEL[formData.frecuencia] ?? formData.frecuencia,
        objectives: {
          shortTerm: formData.objetivoCortoPlayzo.trim(),
          longTerm:  formData.objetivoLargoPlayzo.trim(),
        },
      },
      {
        onSuccess: () => {
          toast({ title: 'Plan de tratamiento creado.', status: 'success', duration: 3000, isClosable: true });
          setIsOpen(false);
          setFormData({ totalSesiones: '10', frecuencia: '3', objetivoCortoPlayzo: '', objetivoLargoPlayzo: '' });
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message ?? 'Error al crear el plan.';
          toast({ title: msg, status: 'error', duration: 4000, isClosable: true });
        },
      }
    );
  };

  const sketchBadge = (
    <Badge display="flex" alignItems="center" px="10px" py="4px" borderRadius="4px" bg={badgeBg} color={badgeColor} borderWidth="1px" borderStyle="dashed" borderColor={badgeColor} fontSize="xs" fontWeight="600">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}>
        <path d="m12.99 6.74 1.93 3.44" /><path d="M19.136 12a10 10 0 0 1-14.271 0" />
        <path d="m21 21-2.16-3.84" /><path d="m3 21 8.02-14.26" /><circle cx="12" cy="5" r="2" />
      </svg>
      Sketch
    </Badge>
  );

  return (
    <Center minH="100vh" bg={bg} p="40px">
      <VStack spacing="24px" w="100%" maxW="700px">
        <Text fontSize="2xl" fontWeight="700">Treatment Plans Preview</Text>
        <Box w="100%" h="1px" bg="gray.200" />
        <Button variant="brand" onClick={() => setIsOpen(true)}>
          Crear Nuevo Plan de Tratamiento
        </Button>
        <Box w="100%" p="16px" bg={headerBg} borderRadius="8px" border="1px solid" borderColor="gray.200">
          <HStack justify="space-between">
            <VStack align="flex-start" spacing="4px">
              <Text fontSize="sm" color="gray.600">Layout Preview:</Text>
              <Text fontSize="sm" fontWeight="600">Caso 1 (ficha) · Caso 2 (externo)</Text>
            </VStack>
            {sketchBadge}
          </HStack>
        </Box>
      </VStack>

      <CustomDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={<HStack spacing="10px"><Text fontWeight="700" fontSize="lg">Nuevo Plan de Tratamiento</Text>{sketchBadge}</HStack>}
        size="lg"
        placement="right"
        width="700px"
        layout="form"
        contentPadding="24px"
        onSave={handleSave}
        isSaving={createPlan.isPending}
        saveButtonText="Guardar Plan"
        cancelButtonText="Cancelar"
      >

        {/* ── Toggle de modo ── */}
        <Box bg={modeBg} borderRadius="10px" p="4px">
          <HStack spacing="0">
            {(['ficha', 'externo'] as Modo[]).map((m) => {
              const isActive = modo === m;
              const label = m === 'ficha' ? 'Desde Ficha del Paciente' : 'Creación Externa';
              return (
                <Box key={m} flex="1" px="16px" py="8px" borderRadius="8px" bg={isActive ? modeActiveBg : 'transparent'} boxShadow={isActive ? 'sm' : 'none'} cursor="pointer" onClick={() => handleModoChange(m)} transition="all 0.15s" textAlign="center">
                  <Text fontSize="sm" fontWeight={isActive ? '700' : '500'} color={isActive ? modeActiveText : modeIdleText} transition="color 0.15s">
                    {label}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        </Box>

        {/* ── CASO 1: auto-seleccionado ── */}
        {modo === 'ficha' && (
          <VStack spacing="10px" align="stretch">
            <HStack spacing="6px">
              <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
                Paciente y Episodio
              </Text>
              <Badge colorScheme="green" fontSize="10px" px="6px" py="1px" borderRadius="4px">Auto-seleccionado</Badge>
            </HStack>
            <PatientEpisodeCard
              patientName={`${MOCK_AUTO_PATIENT.nombres} ${MOCK_AUTO_PATIENT.apellidos}`}
              cedula={MOCK_AUTO_PATIENT.cedula}
              episodeCode={MOCK_AUTO_EPISODE.codigoHc}
              motivo={MOCK_AUTO_EPISODE.motivoConsulta}
              diagnostico={MOCK_AUTO_EPISODE.diagnostico}
              estado={MOCK_AUTO_EPISODE.estado}
            />
          </VStack>
        )}

        {/* ── CASO 2: búsqueda externa ── */}
        {modo === 'externo' && (
          <VStack spacing="12px" align="stretch">
            {/* Buscador */}
            <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
              Buscar Paciente
            </Text>
            <Box position="relative">
              <InputGroup>
                <InputLeftElement h="100%" pointerEvents="none" color={sectionLabel}>
                  <IconSearch />
                </InputLeftElement>
                <Input
                  pl="40px"
                  placeholder="Nombre o número de cédula..."
                  value={searchQuery}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchQuery(val);
                    setShowResults(true);
                    if (selectedPatient && val !== `${selectedPatient.nombres} ${selectedPatient.apellidos}`) {
                      setSelectedPatientId(null);
                      setSelectedEpisodeId(null);
                    }
                  }}
                  onFocus={() => setShowResults(true)}
                  borderRadius="8px"
                  fontSize="sm"
                />
              </InputGroup>

              {/* Dropdown resultados */}
              {showResults && filteredPatients.length > 0 && (
                <Box position="absolute" top="calc(100% + 4px)" left="0" right="0" bg={resultsBg} border="1px solid" borderColor={resultBorder} borderRadius="8px" boxShadow="lg" zIndex="10" overflow="hidden">
                  <List>
                    {filteredPatients.map((p, idx) => (
                      <ListItem
                        key={p.id}
                        px="14px" py="10px"
                        cursor="pointer"
                        _hover={{ bg: resultHover }}
                        borderBottom={idx < filteredPatients.length - 1 ? '1px solid' : 'none'}
                        borderColor={resultBorder}
                        onClick={() => handleSelectPatient(p.id)}
                      >
                        <HStack spacing="10px">
                          <Box w="28px" h="28px" borderRadius="6px" bg={resultIconBg} display="flex" alignItems="center" justifyContent="center" color={resultIconText} flexShrink={0}>
                            <IconUser />
                          </Box>
                          <VStack align="flex-start" spacing="0">
                            <Text fontSize="sm" fontWeight="600" color={resultText}>{p.nombres} {p.apellidos}</Text>
                            <Text fontSize="xs" color={sectionLabel}>CI: {p.cedula}</Text>
                          </VStack>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {showResults && searchQuery.length >= 2 && filteredPatients.length === 0 && (
                <Box position="absolute" top="calc(100% + 4px)" left="0" right="0" bg={resultsBg} border="1px solid" borderColor={resultBorder} borderRadius="8px" p="14px" zIndex="10">
                  <Text fontSize="sm" color={sectionLabel} textAlign="center">Sin resultados para "{searchQuery}"</Text>
                </Box>
              )}
            </Box>

            {/* Selector de episodio */}
            {selectedPatientId && (
              <VStack spacing="8px" align="stretch">
                <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
                  Episodio Clínico (sin plan activo)
                </Text>
                <EpisodeSelector
                  patientId={selectedPatientId}
                  selectedEpisodeId={selectedEpisodeId}
                  onSelect={setSelectedEpisodeId}
                />
              </VStack>
            )}

            {/* Card resumen post-selección */}
            {selectedPatient && selectedEpisode && (
              <>
                <Divider />
                <VStack spacing="8px" align="stretch">
                  <HStack spacing="6px">
                    <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">Selección Confirmada</Text>
                    <Badge colorScheme="green" fontSize="10px" px="6px" py="1px" borderRadius="4px">Listo</Badge>
                  </HStack>
                  <PatientEpisodeCard
                    patientName={`${selectedPatient.nombres} ${selectedPatient.apellidos}`}
                    cedula={selectedPatient.cedula}
                    episodeCode={selectedEpisode.codigoHc}
                    motivo={selectedEpisode.motivoConsulta}
                    diagnostico={selectedEpisode.diagnostico}
                    estado={selectedEpisode.estado}
                  />
                </VStack>
              </>
            )}
          </VStack>
        )}

        {/* Hint cuando caso 2 incompleto */}
        {modo === 'externo' && !showPlanForm && (
          <Box p="14px" borderRadius="8px" bg={hintBg} border="1px solid" borderColor={hintBorder}>
            <Text fontSize="sm" color={hintText}>
              {!selectedPatientId
                ? 'Busca y selecciona un paciente para continuar.'
                : 'Selecciona el episodio clínico al que pertenecerá el plan.'}
            </Text>
          </Box>
        )}

        {/* ── FORMULARIO DEL PLAN ── */}
        {showPlanForm && (
          <>
            <Divider />

            {/* PARÁMETROS GENERALES */}
            <VStack spacing="12px" align="stretch">
              <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
                Parámetros Generales
              </Text>
              <Grid templateColumns="1fr 1fr" gap="12px">
                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600" mb="6px">Total Sesiones</FormLabel>
                    <NumberInput
                      value={formData.totalSesiones}
                      min={1}
                      max={200}
                      onChange={(valStr) => setFormData(p => ({ ...p, totalSesiones: valStr }))}
                      clampValueOnBlur
                    >
                      <NumberInputField borderRadius="8px" fontSize="sm" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600" mb="6px">Frecuencia Sugerida</FormLabel>
                    <Select
                      value={formData.frecuencia}
                      onChange={(e) => setFormData(p => ({ ...p, frecuencia: e.target.value }))}
                      borderRadius="8px"
                      fontSize="sm"
                    >
                      <option value="1">1 vez / sem.</option>
                      <option value="2">2 veces / sem.</option>
                      <option value="3">3 veces / sem.</option>
                      <option value="4">4 veces / sem.</option>
                      <option value="5">5 veces / sem.</option>
                    </Select>
                  </FormControl>
                </GridItem>
              </Grid>
            </VStack>

            <Divider />

            {/* OBJETIVOS TERAPÉUTICOS */}
            <VStack spacing="12px" align="stretch">
              <Text fontSize="xs" fontWeight="700" color={sectionLabel} textTransform="uppercase" letterSpacing="0.08em">
                Objetivos Terapéuticos
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" mb="6px">
                  Objetivo a Corto Plazo
                  <Text as="span" color="red.400" ml="4px" fontSize="xs" fontWeight="400">(Obligatorio)</Text>
                </FormLabel>
                <Textarea
                  placeholder="Ej. Reducir la escala de dolor EVA de 7/10 a 3/10 en las primeras 4 semanas..."
                  value={formData.objetivoCortoPlayzo}
                  onChange={(e) => setFormData(p => ({ ...p, objetivoCortoPlayzo: e.target.value }))}
                  borderRadius="8px"
                  resize="vertical"
                  minH="90px"
                  fontSize="sm"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" mb="6px">
                  Objetivo a Largo Plazo
                  <Text as="span" color="red.400" ml="4px" fontSize="xs" fontWeight="400">(Obligatorio)</Text>
                </FormLabel>
                <Textarea
                  placeholder="Ej. Reincorporar al paciente a su rutina laboral y deportiva sin dolor ni limitación funcional..."
                  value={formData.objetivoLargoPlayzo}
                  onChange={(e) => setFormData(p => ({ ...p, objetivoLargoPlayzo: e.target.value }))}
                  borderRadius="8px"
                  resize="vertical"
                  minH="90px"
                  fontSize="sm"
                />
              </FormControl>
            </VStack>
          </>
        )}
      </CustomDrawer>
    </Center>
  );
}
